// Business-level avisos: the cross-product opportunity signals (stale leads,
// pending bookings, cold properties, tours with traffic but
// no leads) mapped into the same HealthIssue shape as the content avisos.
//
// Lives beside useContentHealth rather than inside it so the data fetching stays
// readable: every query here reuses the exact query key its owning view already
// uses, so opening the bell on a dashboard page hits warm cache instead of
// refetching. Window is fixed at 30 days — avisos are "what's pending now", not
// a period report (that's what /dashboard/analytics is for).
import { fetchSessionsByTour } from "@a/analytics";
import { fetchAllUserLeads } from "@a/leads";
import { fetchCrmProperties } from "@a/properties";
import { fetchUserTours } from "@a/tours";
import { useActiveAccountOptional } from "@ctx/AccountContext";
import { useBookings } from "@h/useBookings";
import { useContactsQuery } from "@h/useContacts";
import { useQuery } from "@tanstack/react-query";
import { buildInsights } from "@u/businessInsights";
import { businessIssues, type HealthIssue } from "@u/contentHealth";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

const WINDOW_DAYS = 30;
const DAY_MS = 86400000;

export function useBusinessHealth(): HealthIssue[] {
  const { t } = useTranslation();
  // "" outside AccountProvider (TourCard on the public portfolio) → every query
  // below stays disabled, so the public surface fetches no owner data.
  const activeOwnerId = useActiveAccountOptional()?.activeOwnerId ?? "";

  const { data: leads = [] } = useQuery({
    queryKey: ["all-user-leads", activeOwnerId],
    queryFn: fetchAllUserLeads,
    enabled: !!activeOwnerId,
    staleTime: 60 * 1000,
  });
  const { data: contacts = [] } = useContactsQuery(activeOwnerId);
  const { data: properties = [] } = useQuery({
    queryKey: ["crm-properties", activeOwnerId],
    queryFn: () => fetchCrmProperties(activeOwnerId),
    enabled: !!activeOwnerId,
    staleTime: 5 * 60 * 1000,
  });
  const { bookings } = useBookings();

  const { data: tours = [] } = useQuery({
    queryKey: ["user-tours", activeOwnerId],
    queryFn: () => fetchUserTours(activeOwnerId),
    enabled: !!activeOwnerId,
    staleTime: 5 * 60 * 1000,
  });
  const tourIds = useMemo(() => tours.map((tr) => tr.id), [tours]);
  const { data: sessionsByTour = {} } = useQuery({
    queryKey: ["sessions-by-tour", tourIds, WINDOW_DAYS],
    queryFn: () => fetchSessionsByTour(tourIds, WINDOW_DAYS),
    enabled: tourIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  // Frozen at mount — a render-time Date.now() would re-run every memo below.
  const [now] = useState(() => Date.now());

  return useMemo(() => {
    const periodStart = now - WINDOW_DAYS * DAY_MS;
    const prevStart = periodStart - WINDOW_DAYS * DAY_MS;
    const at = (iso: string) => new Date(iso).getTime();
    const periodLeads = leads.filter((l) => at(l.created_at) >= periodStart);
    const previousLeads = leads.filter(
      (l) => at(l.created_at) >= prevStart && at(l.created_at) < periodStart,
    );

    const insights = buildInsights(
      {
        now,
        days: WINDOW_DAYS,
        periodLeads,
        previousLeads,
        contacts,
        bookings: bookings.filter((b) => at(b.created_at) >= periodStart),
        properties: properties.map((p) => ({
          status: p.tour.listing?.status ?? null,
          title: p.tour.title,
          leadCount: p.leadCount,
          bookingCount: p.bookingCount,
        })),
        tourTraffic: tours.map((tr) => ({
          id: tr.id,
          title: tr.title,
          sessions: sessionsByTour[tr.id] ?? 0,
          leads: periodLeads.filter(
            (l) => l.source === "360" && l.source_id === tr.id,
          ).length,
        })),
      },
      // No slice: the panel groups and the page paginates by severity, so every
      // live signal should be listed (analytics needed a scannable top-5).
      Number.MAX_SAFE_INTEGER,
    );

    return businessIssues(insights, (k) => t(k));
  }, [
    now,
    leads,
    contacts,
    bookings,
    properties,
    tours,
    sessionsByTour,
    t,
  ]);
}
